import { Badge, Box, Flex, SimpleGrid, Text, useColorModeValue } from '@chakra-ui/react';
import Card from '../../../components/card/Card';
import ErrorState from '../../../components/states/ErrorState';
import { SmartCohortCardDto, SystemCohortType } from '../../../models/churn-prediction/dto/churn-prediction.dto';
import { translate, TranslationKeys } from '../../../i18n';
import { COHORT_TYPE_COLORS } from '../utils/churn.utils';

interface Props {
  cohorts: SmartCohortCardDto[];
  isLoading: boolean;
  isError?: boolean;
}

const COHORT_TYPE_NAMES: Record<SystemCohortType, string> = {
  [SystemCohortType.CHURN_WATCH]: 'churn_prediction.cohort_churn_watch_name',
  [SystemCohortType.CHURN_AT_RISK]: 'churn_prediction.cohort_churn_at_risk_name',
  [SystemCohortType.CHURN_CRITICAL]: 'churn_prediction.cohort_churn_critical_name',
  [SystemCohortType.POWER_USERS]: 'churn_prediction.cohort_power_users_name',
  [SystemCohortType.NEW_USERS]: 'churn_prediction.cohort_new_users_name',
  [SystemCohortType.SOCIAL_CONNECTORS]: 'churn_prediction.cohort_social_connectors_name',
  [SystemCohortType.LAPSED_MEMBERS]: 'churn_prediction.cohort_lapsed_members_name',
  [SystemCohortType.WEEKEND_WARRIORS]: 'churn_prediction.cohort_weekend_warriors_name',
  [SystemCohortType.PAYMENT_ISSUES]: 'churn_prediction.cohort_payment_issues_name',
};

const COHORT_TYPE_DESCS: Record<SystemCohortType, string> = {
  [SystemCohortType.CHURN_WATCH]: 'churn_prediction.cohort_churn_watch_desc',
  [SystemCohortType.CHURN_AT_RISK]: 'churn_prediction.cohort_churn_at_risk_desc',
  [SystemCohortType.CHURN_CRITICAL]: 'churn_prediction.cohort_churn_critical_desc',
  [SystemCohortType.POWER_USERS]: 'churn_prediction.cohort_power_users_desc',
  [SystemCohortType.NEW_USERS]: 'churn_prediction.cohort_new_users_desc',
  [SystemCohortType.SOCIAL_CONNECTORS]: 'churn_prediction.cohort_social_connectors_desc',
  [SystemCohortType.LAPSED_MEMBERS]: 'churn_prediction.cohort_lapsed_members_desc',
  [SystemCohortType.WEEKEND_WARRIORS]: 'churn_prediction.cohort_weekend_warriors_desc',
  [SystemCohortType.PAYMENT_ISSUES]: 'churn_prediction.cohort_payment_issues_desc',
};

export default function SmartCohortCards({ cohorts, isLoading, isError }: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  return (
    <Card alignItems="center" flexDirection="column" w="100%">
      <Flex align="center" w="100%" px="15px" py="10px">
        <Text me="auto" color={textColor} fontSize="xl" fontWeight="700" lineHeight="100%">
          {translate('churn_prediction.smart_cohorts')}
        </Text>
      </Flex>
      {isLoading ? (
        <Flex justify="center" py="40px">
          <Text color="gray.500">{translate('churn_prediction.loading')}</Text>
        </Flex>
      ) : isError ? (
        <ErrorState message={translate('churn_prediction.error_loading')} />
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="12px" w="100%" px="15px" pb="15px">
          {cohorts.map((cohort) => {
            const colorScheme = COHORT_TYPE_COLORS[cohort.systemType] || 'gray';
            return (
              <Box
                key={cohort.id}
                p="14px"
                borderRadius="12px"
                border="1px solid"
                borderColor={borderColor}
                bg={cardBg}
              >
                <Flex justify="space-between" align="center" mb="6px">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {COHORT_TYPE_NAMES[cohort.systemType]
                      ? translate(COHORT_TYPE_NAMES[cohort.systemType] as TranslationKeys)
                      : cohort.name}
                  </Text>
                  <Badge colorScheme={colorScheme} fontSize="xs" textTransform="none">
                    {cohort.memberCount} {translate('churn_prediction.members')}
                  </Badge>
                </Flex>
                <Text color="gray.500" fontSize="xs" noOfLines={2}>
                  {COHORT_TYPE_DESCS[cohort.systemType]
                    ? translate(COHORT_TYPE_DESCS[cohort.systemType] as TranslationKeys)
                    : cohort.description}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </Card>
  );
}
